// ⚠️ BANCO DE PRUEBAS — VARIANTE v2 DEL MODO MODELO. TEMPORAL.
//
// Copia congelada de `panels/DesignModeloPanel.tsx`: la columna derecha del modo
// Modelo (el paso 2, "Modelo + Pose"). Existe para poder rediseñar esa pantalla sin
// tocar el modo que está en producción (`/interfaz-v2/modo/modelo`).
// Ruta de esta variante: `/interfaz-v2/modo/modelo-v2`.
// Ver ADR-2026-08-21-modelo-tres-variantes-de-diseno.
//
// Las dos grillas que llena esta pantalla también son forks propios de la variante
// (`ModelPickerInlineV2` / `PosePickerInlineV2`), así que se pueden rediseñar desde acá.
//
// Un fix de comportamiento en el original NO llega acá: hay que portarlo a mano.
// Cuando el rediseño cierre, lo que gane se promueve al original y este archivo se borra.

// Render del configure panel cuando el preset es 'design-modelo' (o
// 'design-auto', con `hideAutoPickers` para esconder los pickers que la
// IA decide sola). Muestra todas las opciones stackeadas: Modelo
// (inline desktop · botón mobile), Pose (carrusel inline desktop con
// "Ver más" · botón mobile), Fondo (inline desktop · botón mobile),
// Formato y "Otra config".
//
// El botón Generar NO vive acá — lo renderiza el ConfigurePanel padre
// al final, igual que en el resto de los kinds.

import React, { useEffect, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  Pencil,
  Plus,
  Mountain,
  Sparkles,
  User,
  Wand2,
  X,
} from 'lucide-react';
// Las grillas de Modelo y Pose son forks de ESTA variante — es lo que se rediseña acá.
import {
  ModelPickerInline,
  type ModelPickerView,
} from './ModelPickerInline';
import { PosePickerInline } from './PosePickerInline';
// El label del modelo elegido sale del picker original: es texto derivado del
// contrato (`ModelSelection`), no una decisión de diseño de esta pantalla.
import { modelSelectionLabel, type ModelSelection } from '../ModelPicker';
import CachedImg from '../../../components/CachedImg';
import { PromptRecientesButton } from '../components/PromptRecientesButton';
import {
  AjustesFondoBlanco,
  FondoPickerInline,
  fondoSelectionLabel,
  type FondoPickerView,
  type FondoSelection,
} from '../FondoPicker';
import type { PoseV2 } from '../posesApi';
import type { FondoBiblioteca, FondoGuardado } from '../fondosApi';
import type { ModelV2 } from '../modelsApi';
import type { MockProductV2 } from '../mockData';
import { usePalabraDeModelo } from '../../../lib/nombreDeModelo';

// ─── AutoPlaceholder ──────────────────────────────────────────────────
// Visual para cuando Modelo o Pose están en modo "Automático" (sin
// selección). Muestra el ícono del tipo arriba + circulos de color
// (los del gradiente brand) con blur fuerte abajo, mezclados — da una
// vibe "auto/IA" sin la card sólida gradient que se sentía pesada.
const AutoPlaceholder: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
}> = ({ icon: Icon }) => {
  return (
    <div className="absolute inset-0">
      {/* Cluster de círculos blur — anclado al borde inferior, parte
          asomando hacia abajo. Cada círculo drift animado con valores
          de translate grandes para que el movimiento sea VISIBLE
          incluso con blur. Colores: azul, cyan, magenta (más
          contrastantes que los 3 azulados anteriores que se mezclaban
          en un solo tono al blurearse). */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] aspect-square translate-y-[50%]">
        <div
          className="absolute rounded-full blur-2xl animate-drift-1"
          style={{
            width: '60%',
            height: '60%',
            backgroundColor: 'var(--brand-primary)',
            left: '5%',
            top: '20%',
            opacity: 0.95,
          }}
        />
        <div
          className="absolute rounded-full blur-2xl animate-drift-2"
          style={{
            width: '55%',
            height: '55%',
            backgroundColor: '#06B6D4',
            right: '5%',
            top: '15%',
            opacity: 0.85,
          }}
        />
        <div
          className="absolute rounded-full blur-2xl animate-drift-3"
          style={{
            width: '55%',
            height: '55%',
            backgroundColor: '#EC4899',
            left: '25%',
            bottom: '5%',
            opacity: 0.85,
          }}
        />
      </div>
      {/* Icono centrado en la card entera — independiente del cluster. */}
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon className="w-10 h-10 text-neutral-400" />
      </div>
    </div>
  );
};

/** Cuántas secciones se reparten la columna del `configure` a dos columnas. */
const SECCIONES = 2;
/** El `gap-4` que las separa. */
const GAP_SECCIONES = 16;

// ─── SeccionColapsable ────────────────────────────────────────────────
// La sección del `configure` a dos columnas: título + descripción arriba y,
// debajo, la grilla que scrollea con todo el alto que sobra.
//
// Se puede COLAPSAR desde el chevron de la derecha del título. La columna
// tiene un alto fijo —el de la pantalla— y las dos secciones se lo reparten
// en mitades, así que cada una muestra una fila y pico de tarjetas. Colapsar
// la que ya está resuelta es la única forma de ver el catálogo de la otra sin
// scrollear de a una fila: la que queda abierta es `flex-1` y se lleva todo.
//
// Colapsada queda SÓLO el título —que ya trae la cuenta de lo elegido
// ("Modelo · 2 elegidos")— porque es lo único que hace falta saber de una
// sección cerrada. La descripción explica cómo elegir, y ahí no se elige.
//
// El alto de fila se mide POR SECCIÓN y no una vez para las dos: desde que se
// pueden colapsar dejaron de medir lo mismo, que era lo que permitía medir
// una sola.
const SeccionColapsable: React.FC<{
  title: React.ReactNode;
  description: string;
  /** Indicador opcional a la derecha del título. */
  extra?: React.ReactNode;
  abierta: boolean;
  /** Sin handler la sección no se colapsa y no se dibuja el chevron. */
  onToggle?: () => void;
  /** Alto de la columna que scrollea afuera — ver `medirFilaRef`. */
  altoColumna: number | null;
  children: React.ReactNode;
}> = ({
  title,
  description,
  extra,
  abierta,
  onToggle,
  altoColumna,
  children,
}) => {
  /** Alto de una fila de tarjetas. */
  const [altoFila, setAltoFila] = useState<number>(240);
  const seccionRef = useRef<HTMLElement | null>(null);
  const observadoRef = useRef<HTMLDivElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  const medirFilaRef = (el: HTMLDivElement | null) => {
    // Al colapsar, el scroller se desmonta: hay que soltar el observer o queda
    // mirando un nodo muerto y la próxima apertura no vuelve a medir.
    if (!el) {
      roRef.current?.disconnect();
      roRef.current = null;
      observadoRef.current = null;
      return;
    }
    if (observadoRef.current === el) return;
    observadoRef.current = el;
    const medir = () => {
      const gap = 12; // `gap-3`
      // Una fila entera + un ASOMO de la siguiente: `alto + gap + alto*ASOMO = H`
      // (ADR-2026-08-20-grilla-fila-y-media).
      //
      // El asomo bajó de media fila a media (0.5) → cuarto (0.25) → octavo, pedido
      // en ese orden. El criterio: la segunda fila está para AVISAR que el catálogo
      // sigue, no para mirarse. Con media se ve medio cuerpo de cada modelo y
      // compite con la primera —que es la que se usa para decidir— comiéndole un
      // tercio del alto. Un octavo alcanza como corte visible y devuelve ese alto a
      // la primera fila; como la tarjeta es 9:16, crecer de alto la hace más ancha y
      // entran menos por fila, que es la otra mitad del pedido.
      const ASOMO = 0.125;
      // El alto de referencia NO es el que tiene la sección ahora, es el que tendría
      // con las dos abiertas: media columna.
      //
      // Con el real, colapsar una sección agranda las tarjetas de la otra hasta
      // llenar la pantalla con UNA fila —medido: `--alto-fila` saltaba de 256px a
      // 549px y entraba una sola pose por fila—, que es lo contrario de lo que se va
      // a buscar al colapsar. Con el de referencia la tarjeta no cambia de tamaño y
      // el alto liberado se convierte en MÁS FILAS.
      const chrome =
        (seccionRef.current?.clientHeight ?? el.clientHeight) - el.clientHeight;
      const compartido =
        altoColumna === null
          ? Number.POSITIVE_INFINITY
          : (altoColumna - GAP_SECCIONES) / SECCIONES - chrome;
      const alto = Math.min(el.clientHeight, compartido);
      setAltoFila(Math.max(120, Math.round((alto - gap) / (1 + ASOMO))));
    };
    medir();
    roRef.current?.disconnect();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    roRef.current = ro;
  };

  // El alto de referencia depende de la columna, que se mide después del primer
  // render: sin esto la primera medición se queda con el `Infinity` del arranque.
  useEffect(() => {
    const el = observadoRef.current;
    if (!el) return;
    observadoRef.current = null;
    medirFilaRef(el);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [altoColumna]);

  // Sección de MEDIA pantalla: título y descripción arriba, cruzando el ancho, y
  // debajo la grilla con todo el alto que sobra.
  //
  // La descripción va en una línea: con el título a la izquierda —el layout de las
  // otras— se comía 220px de un panel que acá tiene que mostrar dos filas de
  // tarjetas, y el párrafo de tres renglones empujaba la grilla fuera de la
  // sección.
  return (
    <section
      ref={seccionRef}
      className={`rounded-2xl bg-white border border-neutral-200 flex flex-col gap-3 ${
        // Abierta se reparte el alto con la otra; colapsada mide lo que mide su
        // título y le devuelve el resto.
        abierta ? 'flex-1 min-h-0 p-5' : 'shrink-0 px-5 py-3.5'
      }`}
    >
      <div className="shrink-0 flex items-baseline justify-between gap-3">
        {/* Todo el bloque de título es el disparador: un chevron de 32px es un
            blanco chico para algo que se va a usar en cada tanda. */}
        <button
          type="button"
          onClick={onToggle}
          disabled={!onToggle}
          aria-expanded={abierta}
          className="min-w-0 flex-1 text-left disabled:cursor-default"
        >
          <h3 className="text-[20px] font-semibold text-brand-black/90 leading-tight">
            {title}
          </h3>
          {abierta && (
            <p className="text-[12.5px] text-[#3C3C43]/60 mt-0.5 leading-relaxed">
              {description}
            </p>
          )}
        </button>
        <div className="shrink-0 self-center flex items-center gap-2">
          {extra}
          {onToggle && (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={abierta}
              aria-label={abierta ? 'Colapsar la sección' : 'Abrir la sección'}
              title={abierta ? 'Colapsar' : 'Abrir'}
              className="grid h-8 w-8 place-items-center rounded-lg text-brand-black/40 hover:bg-black/[0.04] hover:text-brand-black/70 transition-colors"
            >
              <ChevronDown
                className={`w-[18px] h-[18px] transition-transform ${
                  abierta ? 'rotate-180' : ''
                }`}
              />
            </button>
          )}
        </div>
      </div>
      {/* El scroller de la grilla. `--alto-fila` es el alto de UNA fila, calculado
          para que en el alto disponible entre una fila entera + el asomo de la
          siguiente. Se calcula acá y no en CSS porque no hay forma de expresar "dos
          tercios de mi propio alto" para las filas de un contenido que scrollea. */}
      {abierta && (
        <div
          ref={medirFilaRef}
          className="flex-1 min-h-0 overflow-y-auto"
          style={
            {
              '--alto-fila': `${altoFila}px`,
            } as React.CSSProperties
          }
        >
          {children}
        </div>
      )}
    </section>
  );
};

export interface ModeloPanelProps {
  variant: 'mobile' | 'desktop';
  /** Si true, esconde los pickers de Modelo / Pose / Fondo — usado en
   *  el modo 'design-auto' (Generar imágenes automáticamente) donde
   *  toda la escena la decide IA. */
  hideAutoPickers?: boolean;
  // ── Modo 'design-auto' (cantidad de imágenes) ────────────────────
  /** Cantidad de imágenes a generar — usado para renderizar el
   *  control +/- como card propia cuando `hideAutoPickers` es true. */
  autoImageCount: number;
  onAutoImageCountChange: (v: number) => void;
  autoImageCountMax: number;
  products: MockProductV2[];
  onClearProducts: () => void;
  /** "Añadir producto al outfit" — abre el picker para sumar más productos
   *  a combinar. Solo aplica a design-modelo (no design-auto). */
  onAddProducts?: () => void;
  modelSelection: ModelSelection | null;
  onModelChange: (sel: ModelSelection) => void;
  // ── Serie de modelos (grilla multi-select) ──────────────────────
  // `modelSelection` es el primero; estos, los que siguen. Cada uno genera su
  // propia imagen (ADR-2026-08-20-modelos-multi-select-serie).
  modelSelectionsSerie?: ModelSelection[];
  /** Tap en una card de la grilla: acumula o quita de la serie. */
  onToggleModelSerie?: (sel: ModelSelection) => void;
  // ── Modo "varios modelos" (UI-only) ─────────────────────────────
  modelSelectionsExtra: (ModelSelection | null)[];
  onModelChangeExtras: (next: (ModelSelection | null)[]) => void;
  multiModelEnabled: boolean;
  onMultiModelEnabledChange: (v: boolean) => void;
  onOpenModelPicker: () => void;
  /** Abre el ModelPickerSheet directo a una vista (side panel desktop). */
  onOpenModelSheet: (initialView: ModelPickerView) => void;
  /** Variante del anterior para el sub-flow de multi (slot pre-set). */
  onOpenModelSheetForMultiSlot: (
    slotIdx: number,
    initialView: 'create' | 'upload',
  ) => void;
  /** SOLO DESKTOP. Deja únicamente Modelo y Pose: se van las secciones de Productos,
   *  Fondo y "Otras opciones".
   *
   *  Es para el `configure` a dos columnas, donde el panel de productos sigue a la vista
   *  al lado: Productos repetiría esa grilla, y Fondo y Formato ya están en los ajustes
   *  rápidos de ese panel. Lo que queda acá es lo que NO está en ningún otro lado. */
  soloModeloYPose?: boolean;
  /** Productos asignados por slot (modo varios modelos). */
  multiProductsPerSlot: Record<number, MockProductV2[]>;
  /** Tap en "Utilizando"/miniaturas de un slot del MultiView. */
  onTapSlotProducts: (slotIdx: number) => void;
  userModels: ModelV2[];
  userAuthId: string | null;
  userCompanyId: string | null;
  onModelCreated: () => void;
  modelView: ModelPickerView;
  onModelViewChange: (v: ModelPickerView) => void;
  allPoses: PoseV2[];
  /** Poses propias del cliente. No están en `allPoses` (que es el catálogo
   *  global), así que sin esto una pose propia elegida en el "Ver más" no
   *  aparecería en el carrusel ni en el resumen de seleccionadas. */
  customPoses: PoseV2[];
  poseIds: string[];
  onPoseIdsChange: (ids: string[]) => void;
  /** Toggle "Seleccionar múltiples" del menú de poses (el switch vive
   *  en el PosePickerSheet). Con false, el carrusel inline reemplaza
   *  la selección en cada tap en vez de acumular. */
  multiPoseEnabled: boolean;
  onOpenPosePicker: () => void;
  /** Abre el sheet de poses directo en "Subí tu propia pose" — la tarjeta que cierra la
   *  grilla del `configure` a dos columnas (ADR-2026-08-20-subir-pose-al-final-de-la-grilla). */
  onSubirPose?: () => void;
  bibliotecaFondos: FondoBiblioteca[];
  fondosGuardados: FondoGuardado[];
  fondoSelection: FondoSelection | null;
  onFondoSelectionChange: (sel: FondoSelection) => void;
  onOpenFondoPicker: () => void;
  /** Abre el FondoPickerSheet directo a una vista (describe / upload). */
  onOpenFondoSheet: (
    initialView: FondoPickerView,
    initialCategory?: string,
  ) => void;
  fondoView: FondoPickerView;
  onFondoViewChange: (v: FondoPickerView) => void;
  onFondoSaved: () => void;
  formatoLabel: string;
  onOpenFormat: () => void;
  otraConfig: string;
  onOtraChange: (v: string) => void;
  /** Si true, oculta los botones de add/remove del multi-modelo
   *  (E-commerce: cantidad fija por la pose). */
  lockSlotCount?: boolean;
}

export const ModeloPanel: React.FC<ModeloPanelProps> = ({

  variant,
  hideAutoPickers = false,
  soloModeloYPose = false,
  lockSlotCount = false,
  autoImageCount,
  onAutoImageCountChange,
  autoImageCountMax,
  products,
  onClearProducts,
  onAddProducts,
  modelSelection,
  onModelChange,
  modelSelectionsSerie = [],
  onToggleModelSerie,
  modelSelectionsExtra,
  onModelChangeExtras,
  multiModelEnabled,
  onMultiModelEnabledChange,
  onOpenModelPicker,
  onOpenModelSheet,
  onOpenModelSheetForMultiSlot,
  multiProductsPerSlot,
  onTapSlotProducts,
  userModels,
  userAuthId,
  userCompanyId,
  onModelCreated,
  modelView,
  onModelViewChange,
  allPoses,
  customPoses,
  poseIds,
  onPoseIdsChange,
  multiPoseEnabled,
  onOpenPosePicker,
  onSubirPose,
  bibliotecaFondos,
  fondosGuardados,
  fondoSelection,
  onFondoSelectionChange,
  onOpenFondoPicker,
  onOpenFondoSheet,
  fondoView,
  onFondoViewChange,
  onFondoSaved,
  formatoLabel,
  onOpenFormat,
  otraConfig,
  onOtraChange,
}) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  const isDesktop = variant === 'desktop';
  const modelLabel = modelSelectionLabel(modelSelection);
  const fondoLabel = fondoSelectionLabel(fondoSelection);
  // Dropdown abierto del selector "Cantidad" del modo design-auto.
  // Cierra al elegir o al tap-fuera.
  const [autoCountOpen, setAutoCountOpen] = useState<boolean>(false);
  /**
   * Altos del layout `soloModeloYPose`, los dos medidos.
   *
   * `altoDisponible` es el de la COLUMNA que scrollea afuera de este panel, y de él sale
   * todo lo demás. Se mide ese y no el contenedor propio porque el contenedor no tiene
   * alto definido: los wrappers de `ConfigurePanel` que hay en el medio son de alto
   * automático, así que un `h-full` acá resolvía contra un padre que crecía con el
   * contenido. Y como el contenido son tarjetas cuyo alto sale de esta misma medición,
   * el resultado era un bucle: cada pasada agrandaba el contenedor, que agrandaba las
   * tarjetas. En la práctica llegaba a `--alto-fila: 4194249px` en un par de frames.
   *
   * La columna de afuera, en cambio, es `flex-1` de un alto definido y tiene su propio
   * `overflow`, así que su alto NO depende de lo que pongamos adentro: medirla corta la
   * realimentación.
   */
  const [altoDisponible, setAltoDisponible] = useState<number | null>(null);
  const observadoColumnaRef = useRef<HTMLElement | null>(null);
  /**
   * Las secciones que el usuario colapsó, por clave ('modelo' / 'pose'). Sólo
   * aplica al layout `soloModeloYPose`, que es el único donde las secciones se
   * reparten un alto fijo y colapsar una le sirve de algo a la otra.
   *
   * Arrancan las dos abiertas: colapsar es una decisión del momento —"el modelo
   * ya está, dejame ver poses"— y no una preferencia que valga la pena recordar.
   */
  const [colapsadas, setColapsadas] = useState<string[]>([]);
  const alternarColapso = (clave: string) =>
    setColapsadas((prev) =>
      prev.includes(clave) ? prev.filter((c) => c !== clave) : [...prev, clave],
    );
  const autoCountRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!autoCountOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (!autoCountRef.current?.contains(e.target as Node)) {
        setAutoCountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [autoCountOpen]);
  // Etiqueta amigable del valor actual ("1 imagen" / "N imágenes").
  const autoCountLabel = `${autoImageCount} ${
    autoImageCount === 1 ? 'imagen' : 'imágenes'
  }`;

  // Auto-resize del textarea "Instrucción adicional" en desktop. Crece
  // con cada nueva línea de texto sin mostrar el handle de resize en la
  // esquina. Se ajusta a `otraConfig` (cambia tras setear value o tras
  // limpiar). Reset a 'auto' antes de medir scrollHeight para que pueda
  // achicarse al borrar líneas.
  const otraTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const ta = otraTextareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    // Cap a 6 líneas exactas: computamos line-height + padding del
    // textarea para calcular la altura máxima en px (independiente
    // del viewport). Cuando el contenido supera, el textarea scrollea
    // internamente; cuando entra, sin scrollbar.
    const cs = window.getComputedStyle(ta);
    const lineH = parseFloat(cs.lineHeight) || 20;
    const padT = parseFloat(cs.paddingTop) || 0;
    const padB = parseFloat(cs.paddingBottom) || 0;
    const maxH = lineH * 6 + padT + padB;
    const target = Math.min(ta.scrollHeight, maxH);
    ta.style.height = `${target}px`;
    ta.style.overflowY = ta.scrollHeight > maxH ? 'auto' : 'hidden';
  }, [otraConfig]);

  if (isDesktop) {
    /** El ancestro que scrollea: el primero para arriba con `overflow-y` auto/scroll. */
    const buscarColumnaScrolleable = (el: HTMLElement): HTMLElement | null => {
      let p = el.parentElement;
      while (p) {
        const oy = getComputedStyle(p).overflowY;
        if (oy === 'auto' || oy === 'scroll') return p;
        p = p.parentElement;
      }
      return null;
    };

    /** Ref del contenedor de las secciones: mide la columna de afuera. */
    const medirColumnaRef = (el: HTMLDivElement | null) => {
      if (!el) return;
      const columna = buscarColumnaScrolleable(el);
      if (!columna || observadoColumnaRef.current === columna) return;
      observadoColumnaRef.current = columna;
      const medir = () => setAltoDisponible(columna.clientHeight);
      medir();
      new ResizeObserver(medir).observe(columna);
    };

    /** Las dos secciones cerradas: ya no hay quién use el alto de la columna. */
    const todasColapsadas =
      colapsadas.includes('modelo') && colapsadas.includes('pose');

    // Cada sección es una card independiente con título (Playfair) +
    // descripción a la izquierda y el contenido del picker a la derecha.
    // Layout horizontal por card → fixed-width sidebar de título + área
    // de contenido flexible.
    //
    // En modo 'design-auto' (hideAutoPickers=true) el panel se
    // renderiza en el aside lateral derecho (~420-460px de ancho), que
    // es muy estrecho para el layout horizontal. Ahí usamos layout
    // VERTICAL — título y descripción arriba, contenido full-width
    // abajo — igual que el mobile.
    /**
     * Título de sección con la cuenta de lo elegido: "Modelo · 2 elegidos".
     *
     * El número va EN el título porque en esta pantalla las dos grillas scrollean y
     * muestran una fila y pico: lo elegido puede estar fuera de vista, y sin la cuenta la
     * única forma de saber cuántas van era scrollear buscando tildes.
     *
     * En cero no se escribe nada —"Pose · 0 elegidas" es una forma larga de decir que no
     * empezaste— y sólo aparece en `soloModeloYPose`: en el layout ancho el título tiene
     * la descripción al lado y la grilla entera a la vista.
     */
    const tituloConCuenta = (
      titulo: string,
      cantidad: number,
      singular: string,
      plural: string,
    ): React.ReactNode =>
      !soloModeloYPose || cantidad === 0 ? (
        titulo
      ) : (
        <>
          {titulo}{' '}
          <span className="font-medium text-[#3C3C43]/45">
            · {cantidad} {cantidad === 1 ? singular : plural}
          </span>
        </>
      );

    const sectionWrap = (
      // `ReactNode` y no `string`: los títulos de Modelo y Pose llevan la cuenta de lo
      // elegido en un tono más suave, y eso es markup.
      title: React.ReactNode,
      description: string,
      content: React.ReactNode,
      // Indicador opcional que va DEBAJO de la descripción (ej: cantidad de
      // poses seleccionadas).
      extra?: React.ReactNode,
      // Clave de colapso. Sin ella la sección no se puede cerrar — es el caso de
      // las que no compiten por el alto de la columna.
      claveColapso?: string,
    ) =>
      soloModeloYPose ? (
        <SeccionColapsable
          title={title}
          description={description}
          extra={extra}
          altoColumna={altoDisponible}
          abierta={!claveColapso || !colapsadas.includes(claveColapso)}
          onToggle={
            claveColapso ? () => alternarColapso(claveColapso) : undefined
          }
        >
          {content}
        </SeccionColapsable>
      ) : hideAutoPickers ? (
        <section className="rounded-2xl bg-white border border-neutral-200 p-5 flex flex-col gap-3">
          <div>
            <h3 className="text-[17px] font-semibold text-brand-black/90 leading-tight">
              {title}
            </h3>
            <p className="text-[12.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
              {description}
            </p>
            {extra}
          </div>
          <div className="min-w-0">{content}</div>
        </section>
      ) : (
        <section className="rounded-2xl bg-white border border-neutral-200 p-6 flex gap-6">
          <div className="w-[220px] shrink-0 flex flex-col">
            <div>
              <h3 className="text-[20px] font-semibold text-brand-black/90 leading-tight">
                {title}
              </h3>
              <p className="text-[12.5px] text-[#3C3C43]/60 mt-1.5 leading-relaxed">
                {description}
              </p>
            </div>
            {extra && <div className="mt-auto pt-3">{extra}</div>}
          </div>
          <div className="flex-1 min-w-0">{content}</div>
        </section>
      );
    return (
      <div
        ref={soloModeloYPose ? medirColumnaRef : undefined}
        className="flex flex-col gap-4"
        style={
          // Alto EXPLÍCITO —no `h-full`— porque los wrappers de `ConfigurePanel` que hay
          // arriba son de alto automático y el porcentaje no tendría contra qué resolver.
          // Con esto las dos secciones (`flex-1`) se reparten mitades reales.
          //
          // Con las DOS colapsadas se suelta: nadie reclama el alto, y forzarlo dejaría
          // media pantalla en blanco debajo de dos títulos.
          soloModeloYPose && altoDisponible && !todasColapsadas
            ? { height: altoDisponible }
            : undefined
        }
      >
        {!soloModeloYPose &&
        sectionWrap(
          'Productos',
          'Vas a generar la imagen con estos productos.',
          <div className="space-y-2.5">
          <div className="flex items-center gap-3 bg-[#F7F7F9] rounded-2xl p-3">
            {products.length === 1 ? (
              <CachedImg
                src={products[0].imageUrl}
                alt={products[0].name}
                className="w-12 h-12 rounded-lg object-cover shrink-0"
              />
            ) : (
              <div className="flex shrink-0 -space-x-2">
                {products.slice(0, 3).map((p) => (
                  <CachedImg
                    key={p.id}
                    src={p.imageUrl}
                    alt={p.name}
                    className="w-10 h-10 rounded-lg object-cover ring-2 ring-[#F7F7F9]"
                  />
                ))}
                {products.length > 3 && (
                  <div className="w-10 h-10 rounded-lg bg-white ring-2 ring-[#F7F7F9] flex items-center justify-center text-[12px] font-semibold text-brand-black/70">
                    +{products.length - 3}
                  </div>
                )}
              </div>
            )}
            <p className="flex-1 min-w-0 text-[14.5px] font-semibold text-brand-black/90 truncate">
              {products.length === 1
                ? products[0].name
                : products.map((p) => p.name).join(', ')}
            </p>
            <button
              type="button"
              onClick={onClearProducts}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-black/[0.04] transition-colors shrink-0"
              aria-label="Cambiar selección"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {!hideAutoPickers && onAddProducts && (
            <button
              type="button"
              onClick={onAddProducts}
              className="w-full inline-flex items-center justify-center gap-2 py-3 px-5 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/80 hover:text-brand-primary text-[14px] font-medium transition-colors"
            >
              {products.length > 1 ? (
                <>
                  <Pencil className="w-4 h-4" />
                  Editar outfit
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Añadir producto al outfit
                </>
              )}
            </button>
          )}
          </div>,
        )}
        {!hideAutoPickers &&
          sectionWrap(
            // El principal + los de la serie. El "Automático" también cuenta: es el
            // modelo con el que se va a generar. Sin principal la serie está vacía
            // también (se limpian juntos), así que esto es 0 cuando no hay nada.
            tituloConCuenta(
              palabra.titulo,
              (modelSelection ? 1 : 0) + modelSelectionsSerie.length,
              'elegido',
              'elegidos',
            ),
            soloModeloYPose
              ? // Media pantalla es poco lugar para tres renglones: la versión corta dice
                // lo único que no se deduce mirando la grilla.
                'Elegí uno o varios — generamos una imagen por cada uno.'
              : 'Quién va a aparecer en la imagen. Podés elegir uno guardado, crear uno nuevo o subir tus propias fotos.',
            <ModelPickerInline
              selection={modelSelection}
              onChange={onModelChange}
              view={modelView}
              onViewChange={onModelViewChange}
              userModels={userModels}
              userId={userAuthId}
              companyId={userCompanyId}
              onModelCreated={onModelCreated}
              hideLabel
              grilla={soloModeloYPose}
              // Multi-select sólo en la grilla: el carrusel y el sheet siguen
              // siendo de selección única (tap reemplaza).
              selectionsSerie={soloModeloYPose ? modelSelectionsSerie : []}
              onToggleSelection={
                soloModeloYPose ? onToggleModelSerie : undefined
              }
              onOpenSheet={onOpenModelSheet}
              onOpenSheetForMultiSlot={onOpenModelSheetForMultiSlot}
              selectionsExtra={modelSelectionsExtra}
              onChangeExtras={onModelChangeExtras}
              onMultiEnabledChange={onMultiModelEnabledChange}
              multiProducts={products.map((p) => ({
                id: p.id,
                name: p.name,
                imageUrl: p.imageUrl,
              }))}
              multiProductsPerSlot={multiProductsPerSlot}
              onTapSlotProducts={onTapSlotProducts}
              lockSlotCount={lockSlotCount}
            />,
            // Acá iba el aviso "Usar varios modelos consume más créditos" en el slot
            // `extra` de la sección. Se sacó: el costo real de la tanda ya está impreso
            // en la pastilla del botón Generar y en el resumen que tiene encima
            // ("N imágenes · M modelos × P poses"), así que el aviso repetía en gris —y
            // en condicional— lo que dos controles dicen con el número exacto.
            undefined,
            'modelo',
          )}
        {!hideAutoPickers &&
          sectionWrap(
            tituloConCuenta('Pose', poseIds.length, 'elegida', 'elegidas'),
            soloModeloYPose
              ? // Ya no hay que activar nada: en esta pantalla el tap acumula, así que la
                // instrucción de la versión larga sobraba. Queda lo que sí importa saber
                // antes de elegir varias: que cada una cuesta una imagen.
                //
                // Con varios modelos la cuenta deja de ser "una por pose": cada pose se
                // genera CON CADA MODELO, así que la frase lo dice explícito (el total
                // sale multiplicado, y el resumen de arriba del Generar lo confirma).
                modelSelectionsSerie.length > 0
                ? `Elegí una o varias — generamos una imagen de cada pose con cada uno de los ${
                    1 + modelSelectionsSerie.length
                  } modelos.`
                : 'Elegí una o varias — generamos una imagen por cada una.'
              : `Elegí la pose de tu ${palabra.singular}. Para usar varias, activá "Seleccionar múltiples" desde Ver más — generamos una imagen por cada una.`,
            <PosePickerInline
              poses={allPoses}
              customPoses={customPoses}
              selectedIds={poseIds}
              onChange={onPoseIdsChange}
              onOpenSheet={onOpenPosePicker}
              // Sólo en la grilla: es la variante que muestra el catálogo entero y puede
              // cerrarlo con la tarjeta de subir.
              onSubirPose={soloModeloYPose ? onSubirPose : undefined}
              hideLabel
              // En multi-modelo (>1 slot activo) la pose es selección
              // única: no soportamos generar variantes-por-pose con
              // varios modelos.
              singleSelect={
                multiModelEnabled && modelSelectionsExtra.length > 0
              }
              // Con el toggle "Seleccionar múltiples" (del sheet) apagado,
              // el carrusel también reemplaza en vez de acumular. En
              // `soloModeloYPose` la multi-selección está SIEMPRE activa: la grilla
              // muestra el catálogo entero y pedir que el usuario prenda un toggle
              // escondido en otra pantalla para acumular no tiene sentido acá.
              multiPoseEnabled={soloModeloYPose || multiPoseEnabled}
              // Se puede sacar la última: acá la cuenta está en el título y el CTA se
              // apaga en cero.
              permitirVacio={soloModeloYPose}
              grilla={soloModeloYPose}
            />,
            undefined,
            'pose',
          )}
        {!hideAutoPickers &&
          !soloModeloYPose &&
          sectionWrap(
            'Fondo',
            `El escenario donde aparece el ${palabra.singular}. Elegí de la biblioteca, subí uno propio o describilo en palabras.`,
            <FondoPickerInline
              selection={fondoSelection}
              onChange={onFondoSelectionChange}
              view={fondoView}
              onViewChange={onFondoViewChange}
              biblioteca={bibliotecaFondos}
              guardados={fondosGuardados}
              companyId={userCompanyId}
              onFondoSaved={onFondoSaved}
              hideLabel
              onOpenSheet={onOpenFondoSheet}
              selectedProductNames={products.map((p) => p.name)}
            />,
          )}
        {!soloModeloYPose &&
        sectionWrap(
          hideAutoPickers ? 'Opciones' : 'Otras opciones',
          'Definí el formato de salida y agregá indicaciones extras para afinar el resultado.',
          <div className="flex flex-col gap-2.5">
            {/* Selector Cantidad — solo modo design-auto. Mismo
                container que el botón Formato (bg-white rounded-2xl,
                label izquierda + valor derecha) pero al tap despliega
                un dropdown absolute con los valores 1..N. */}
            {hideAutoPickers && (
              <div ref={autoCountRef} className="relative">
                <button
                  type="button"
                  onClick={() => setAutoCountOpen((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={autoCountOpen}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
                >
                  <span className="text-[13.5px] text-brand-black/85">
                    Cantidad
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-black/85">
                    {autoCountLabel}
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-[#3C3C43]/55 transition-transform ${
                        autoCountOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </span>
                </button>
                {autoCountOpen && (
                  <ul
                    role="listbox"
                    aria-label="Cantidad de imágenes"
                    className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 bg-white border border-neutral-200 rounded-2xl shadow-lg overflow-hidden animate-mode-swap"
                  >
                    {Array.from(
                      { length: autoImageCountMax },
                      (_, i) => i + 1,
                    ).map((n) => {
                      const selected = n === autoImageCount;
                      const label = `${n} ${n === 1 ? 'imagen' : 'imágenes'}`;
                      return (
                        <li key={n}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => {
                              onAutoImageCountChange(n);
                              setAutoCountOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-[13.5px] transition-colors flex items-center justify-between ${
                              selected
                                ? 'bg-brand-primary/[0.07] text-brand-primary font-semibold'
                                : 'text-brand-black/85 hover:bg-neutral-50'
                            }`}
                          >
                            <span>{label}</span>
                            {selected && (
                              <Check className="w-3.5 h-3.5" strokeWidth={3} />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={onOpenFormat}
              className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
            >
              <span className="text-[13.5px] text-brand-black/85">
                Formato
              </span>
              <span className="text-[13.5px] font-medium text-brand-black/85">
                {formatoLabel}
              </span>
            </button>
            <div className="relative">
              <textarea
                ref={otraTextareaRef}
                value={otraConfig}
                onChange={(e) => onOtraChange(e.target.value)}
                placeholder="Instrucción adicional"
                rows={3}
                className="w-full resize-none overflow-hidden px-4 pt-3 pb-11 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
              />
              <PromptRecientesButton
                tipo="instruccion"
                onPick={onOtraChange}
                titulo="Instrucciones usadas antes"
                className="absolute bottom-2.5 right-2.5"
              />
            </div>
          </div>,
        )}
      </div>
    );
  }

  // ── Mobile: cards cuadradas para Modelo+Pose (con thumbs visuales)
  //    + botones row Fondo/Formato + textarea Otra config. ─────────────
  // Resolver signed URL fresca para un slot 'preset' usando
  // `userModels` — la URL stored en la selección puede ser stale.
  const resolveFreshModelUrl = (sel: ModelSelection | null): string | null => {
    if (!sel || sel.mode !== 'preset') return null;
    const fresh = userModels.find((m) => String(m.id) === sel.id);
    return fresh?.thumbnailUrl || sel.imageUrl || null;
  };
  // Thumbnail del modelo seleccionado (preset) o gradiente fallback.
  const modelThumbUrl = resolveFreshModelUrl(modelSelection);
  // Poses seleccionadas resueltas a objetos completos (con imageUrl). Se busca
  // en el catálogo Y en las propias: desde que se pueden mezclar las dos en una
  // tanda, buscar solo en `allPoses` dejaba afuera a las propias.
  const selectedPoses = poseIds
    .map(
      (id) =>
        allPoses.find((p) => p.id === id) ??
        customPoses.find((p) => p.id === id),
    )
    .filter((p): p is PoseV2 => !!p);
  // Multi-modelo activo con al menos un slot extra: armamos un array
  // unificado [principal, ...extras] y mostramos el stack rotado
  // (mismo patrón visual que multi-pose). Cada slot reduce a su
  // `imageUrl` (preset → la url; auto/custom/null → null → gris).
  const isMultiModel = multiModelEnabled && modelSelectionsExtra.length > 0;
  const multiModelThumbs: { imageUrl: string | null }[] = isMultiModel
    ? [modelSelection, ...modelSelectionsExtra].map((s) => ({
        imageUrl: resolveFreshModelUrl(s),
      }))
    : [];

  return (
    <div className="flex flex-col gap-2.5">
      {/* Modelo + Pose en 2 columnas, cuadradas. */}
      {!hideAutoPickers && (
      <div className="grid grid-cols-2 gap-2.5">
        {/* MODELO — sin nombre debajo. Thumb 4:5 anclado arriba del card
            (debajo del label), su altura excede el espacio disponible y
            el `overflow-hidden` del card recorta la parte inferior, dando
            el efecto de "salir del borde inferior". */}
        <button
          type="button"
          onClick={onOpenModelPicker}
          className="relative aspect-square rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors overflow-hidden text-left min-w-0"
        >
          <span className="absolute top-3 left-3 z-10 text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
            <User className="w-3 h-3" />
            {palabra.titulo}
          </span>
          {isMultiModel ? (
            // Multi-modelo: stack rotado en abanico, mismo algoritmo
            // visual que el multi-pose (THUMB_W%, OFFSET_X%,
            // ROTATE_STEP°). Cada slot sin thumb (auto/custom/null)
            // cae al placeholder gris.
            <div className="absolute inset-x-3 top-9 aspect-[2/3]">
              {(() => {
                const slots = multiModelThumbs.slice(0, 4);
                const THUMB_W = 80;
                const OFFSET_X = 12;
                const ROTATE_STEP = 11;
                const totalSpreadX =
                  THUMB_W + Math.max(0, slots.length - 1) * OFFSET_X;
                const startX = Math.max(0, (100 - totalSpreadX) / 2);
                return (
                  <>
                    {slots.map((s, i) => {
                      const leftPct = startX + i * OFFSET_X;
                      const z = slots.length - i;
                      const rotation =
                        (i - (slots.length - 1) / 2) * ROTATE_STEP;
                      return (
                        <div
                          key={i}
                          // bg-neutral-200 sirve de placeholder cuando la
                          // signed URL caduca o falla — la `<img>` se
                          // oculta con onError y queda este fondo.
                          className="absolute top-0 aspect-[2/3] rounded-[16px] overflow-hidden ring-2 ring-white shadow-sm bg-neutral-200"
                          style={{
                            left: `${leftPct}%`,
                            width: `${THUMB_W}%`,
                            zIndex: z,
                            transform: `rotate(${rotation}deg)`,
                            transformOrigin: 'center top',
                          }}
                        >
                          {s.imageUrl && (
                            <CachedImg
                              src={s.imageUrl}
                              alt=""
                              className="absolute inset-0 w-full h-full object-cover object-top"
                              onError={(e) => {
                                e.currentTarget.style.visibility = 'hidden';
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          ) : modelThumbUrl ? (
            <div className="absolute inset-x-3 top-9 aspect-[2/3] rounded-[20px] overflow-hidden ring-1 ring-black/[0.05] bg-neutral-200">
              <CachedImg
                src={modelThumbUrl}
                alt={modelLabel}
                className="absolute inset-0 w-full h-full object-cover object-top"
                onError={(e) => {
                  e.currentTarget.style.visibility = 'hidden';
                }}
              />
            </div>
          ) : (
            // Auto: el placeholder ocupa toda la card (no el wrapper
            // chico) para que ícono + círculos queden visualmente
            // centrados en el cuadrado completo.
            <AutoPlaceholder icon={Sparkles} />
          )}
        </button>

        {/* POSE — mismo patrón. Multi: stack offset desde la esquina. */}
        <button
          type="button"
          onClick={onOpenPosePicker}
          className="relative aspect-square rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors overflow-hidden text-left min-w-0"
        >
          <span className="absolute top-3 left-3 z-10 text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
            <Wand2 className="w-3 h-3" />
            Pose
          </span>
          {selectedPoses.length === 0 ? (
            // Auto: placeholder centrado en toda la card (full inset).
            <AutoPlaceholder icon={Wand2} />
          ) : (
          <div
            className={`absolute inset-x-3 top-9 aspect-[2/3] ${
              selectedPoses.length === 1
                ? 'rounded-[20px] overflow-hidden ring-1 ring-black/[0.05]'
                : ''
            }`}
          >
            {selectedPoses.length === 1 ? (
              <img
                src={selectedPoses[0].imageUrl}
                alt={selectedPoses[0].nombre}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            ) : (
              // Stack acordeón vertical: hasta 3 thumbs reales + 1 slot
              // gris claro como "hay más" si selectedPoses > 3. Cada slot
              // se anchora arriba con offset Y incremental — su parte
              // inferior queda oculta detrás del próximo, creando la
              // sensación de cartas plegadas hacia abajo.
              (() => {
                const visible = selectedPoses.slice(0, 3);
                const showGray = selectedPoses.length > 3;
                type Slot = { kind: 'pose'; p: PoseV2 } | { kind: 'gray' };
                const slots: Slot[] = [
                  ...visible.map((p): Slot => ({ kind: 'pose', p })),
                  ...(showGray ? [{ kind: 'gray' } as Slot] : []),
                ];
                // Cartas en abanico horizontal, todas a la misma altura.
                // Z-index DECRECIENTE: la primera (izquierda) queda
                // arriba de la pila, cada siguiente (a la derecha) va
                // una capa más abajo — la izquierda overlapea sobre la
                // de la derecha. Width grande + aspect 2:3 hace que su
                // altura exceda el outer card, así su parte inferior
                // queda cortada por el `overflow-hidden` del card
                // (mismo efecto que la imagen del modelo solo).
                const THUMB_W = 80; // % del wrapper — alto para clip bottom
                const OFFSET_X = 12; // % horizontal por slot
                const ROTATE_STEP = 11; // grados por slot — fan marcado
                const totalSpreadX =
                  THUMB_W + Math.max(0, slots.length - 1) * OFFSET_X;
                const startX = Math.max(0, (100 - totalSpreadX) / 2);
                return (
                  <>
                    {slots.map((s, i) => {
                      const leftPct = startX + i * OFFSET_X;
                      // z decreciente: i=0 → mayor, i=N-1 → menor.
                      const z = slots.length - i;
                      const rotation =
                        (i - (slots.length - 1) / 2) * ROTATE_STEP;
                      const key =
                        s.kind === 'pose' ? `p-${s.p.id}` : `gray-${i}`;
                      return (
                        <div
                          key={key}
                          className="absolute top-0 aspect-[2/3] rounded-[16px] overflow-hidden ring-2 ring-white shadow-sm"
                          style={{
                            left: `${leftPct}%`,
                            width: `${THUMB_W}%`,
                            zIndex: z,
                            transform: `rotate(${rotation}deg)`,
                            transformOrigin: 'center top',
                          }}
                        >
                          {s.kind === 'pose' ? (
                            <img
                              src={s.p.imageUrl}
                              alt={s.p.nombre}
                              className="absolute inset-0 w-full h-full object-cover object-top"
                            />
                          ) : (
                            <div className="absolute inset-0 bg-neutral-200" />
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()
            )}
          </div>
          )}
        </button>
      </div>
      )}

      {/* Fondo + Formato siguen como rows planas. */}
      {!hideAutoPickers && (
      <button
        type="button"
        onClick={onOpenFondoPicker}
        className="flex flex-col items-start text-left px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors min-w-0"
      >
        <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
          <Mountain className="w-3 h-3" />
          Fondo
        </span>
        <span className="text-[14.5px] font-semibold text-brand-black/90 mt-0.5 truncate w-full">
          {fondoLabel}
        </span>
      </button>
      )}
      {/* Ajustes del fondo blanco: acá y no adentro del picker porque en mobile el
          picker es un sheet que se CIERRA al elegir "Fondo blanco" — los controles
          nunca se habrían visto. Devuelve null solo con fondo blanco elegido. */}
      {!hideAutoPickers && (
        <AjustesFondoBlanco
          selection={fondoSelection}
          onChange={onFondoSelectionChange}
          // Sub-panel gris de la row de arriba (mismo tratamiento que los chip
          // blocks del panel). `p-2` con radio 16 exterior deja los chips de
          // adentro en 8 y los radios quedan concéntricos.
          className="bg-[#F7F7F9] rounded-2xl p-2"
        />
      )}
      {/* Cantidad — card propia solo en modo 'design-auto', arriba del
          Formato. Layout horizontal: label izquierda · valor +
          chevron derecha. Tap abre un dropdown absolute con 1..N
          imágenes (mismo patrón que el desktop). */}
      {hideAutoPickers && (
        <div ref={autoCountRef} className="relative">
          <button
            type="button"
            onClick={() => setAutoCountOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={autoCountOpen}
            className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
          >
            <span className="text-[14.5px] font-normal text-brand-black/90">
              Cantidad
            </span>
            <span className="inline-flex items-center gap-1.5 text-[14.5px] font-semibold text-brand-black/90">
              {autoCountLabel}
              <ChevronDown
                className={`w-3.5 h-3.5 text-[#3C3C43]/55 transition-transform ${
                  autoCountOpen ? 'rotate-180' : ''
                }`}
              />
            </span>
          </button>
          {autoCountOpen && (
            <ul
              role="listbox"
              aria-label="Cantidad de imágenes"
              className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 bg-white border border-neutral-200 rounded-2xl shadow-lg overflow-hidden animate-mode-swap"
            >
              {Array.from(
                { length: autoImageCountMax },
                (_, i) => i + 1,
              ).map((n) => {
                const selected = n === autoImageCount;
                const label = `${n} ${n === 1 ? 'imagen' : 'imágenes'}`;
                return (
                  <li key={n}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        onAutoImageCountChange(n);
                        setAutoCountOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-[14.5px] transition-colors flex items-center justify-between ${
                        selected
                          ? 'bg-brand-primary/[0.07] text-brand-primary font-semibold'
                          : 'text-brand-black/85 hover:bg-neutral-50'
                      }`}
                    >
                      <span>{label}</span>
                      {selected && (
                        <Check className="w-4 h-4" strokeWidth={3} />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onOpenFormat}
        className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
      >
        <span className="text-[14.5px] font-normal text-brand-black/90">
          Formato
        </span>
        <span className="text-[14.5px] font-semibold text-brand-black/90 truncate">
          {formatoLabel}
        </span>
      </button>
      {/* Textarea auto-grow: crece con las líneas hasta max-h-[45vh],
          después scrollea internamente. La altura se ajusta vía
          useEffect (otraTextareaRef + watchea otraConfig). */}
      <div className="relative">
        <textarea
          ref={otraTextareaRef}
          value={otraConfig}
          onChange={(e) => onOtraChange(e.target.value)}
          placeholder="Instrucción adicional"
          rows={2}
          className="w-full px-4 pt-3 pb-11 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors resize-none"
        />
        <PromptRecientesButton
          tipo="instruccion"
          onPick={onOtraChange}
          titulo="Instrucciones usadas antes"
          className="absolute bottom-2.5 right-2.5"
        />
      </div>
    </div>
  );
};
